import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPath?: string;
  requiredPermission?: 'read' | 'write' | 'delete' | 'export';
}

export default function ProtectedRoute({ children, requiredPath, requiredPermission }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { canAccessPath, hasPermission, loading: permLoading } = usePermissions();
  const location = useLocation();

  if (loading || permLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check path-based permission if specified
  if (requiredPath) {
    const pathAllowed = requiredPermission
      ? hasPermission(requiredPath, requiredPermission)
      : canAccessPath(requiredPath);

    if (!pathAllowed) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
          <LockIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography variant="h5" color="text.secondary">Access Denied</Typography>
          <Typography variant="body2" color="text.disabled">You don't have permission to view this page.</Typography>
          <Button variant="outlined" onClick={() => window.history.back()}>Go Back</Button>
        </Box>
      );
    }
  }

  return <>{children}</>;
}